import {
  playdataNewsDetailPageQueryOptions,
  playdataNewsListPageQueryOptions,
} from "@data/public-page-data-query"
import * as m from "@paraglide/messages"
import { sanitizeRichHtml } from "@playbrain/content-types/richHtml"
import { PlaydataButtonLink, PlaydataFigure, PlaydataSection } from "@playdata/PlaydataPrimitives"
import { playdataPublishDate } from "@playdata/playdata-date"
import { playdataNewsCoverSrc } from "@playdata/playdata-news-covers"
import type { NewsItem, SupportedLocale } from "@site-types/content"
import type { NewsDetailPageData, NewsListPageData } from "@site-types/public-page-data"
import { createFileRoute, Link, notFound } from "@tanstack/react-router"
import { playdataRouteHead } from "@utils/playdata-route-head"

export const Route = createFileRoute("/$locale/playdata/news/$slug")({
  loader: async ({ context, params }) => {
    const locale = params.locale as SupportedLocale
    const data = await context.queryClient.ensureQueryData(
      playdataNewsDetailPageQueryOptions(locale, params.slug)
    )
    if (!data) throw notFound()

    /*
     * The neighbours come from the same list the archive renders, so "next"
     * means next in the archive — the live site's own sense of it. A failure
     * here costs the two links at the foot of the article rather than the
     * article, which is why it is caught rather than awaited into the page.
     */
    const list = await context.queryClient
      .ensureQueryData(playdataNewsListPageQueryOptions(locale))
      .catch(() => null)

    return { ...data, siblings: adjacentArticles((list as NewsListPageData | null)?.news, params.slug) }
  },
  head: ({ loaderData, params }) => {
    const locale = params.locale as SupportedLocale
    const post = (loaderData as NewsDetailPageData | undefined)?.post
    return playdataRouteHead({
      description: post?.metaDescription ?? post?.newsCoverBlurb ?? "",
      locale,
      path: `/playdata/news/${params.slug}`,
      title: post?.metaOgTitle ?? post?.title ?? m.playdata_news_meta_title({}, { locale }),
      type: "article",
    })
  },
  component: RouteComponent,
})

/** The live article: a large centred title, the excerpt beneath, then the body. */
/**
 * The article either side of this one, in archive order.
 *
 * The list is newest first, so the entry *after* this one is the older
 * article — which is what the live site labels "Next".
 */
function adjacentArticles(news: NewsItem[] | undefined, slug: string) {
  const items = news ?? []
  const index = items.findIndex((item) => item.slug?.current === slug)
  if (index === -1) return { next: null, previous: null }
  return {
    next: items[index + 1] ?? null,
    previous: items[index - 1] ?? null,
  }
}

function ArticleLink({
  article,
  direction,
  label,
  locale,
}: {
  article: NewsItem
  direction: "next" | "previous"
  label: string
  locale: SupportedLocale
}) {
  return (
    <Link
      className={`flex flex-col gap-[0.4rem] ${direction === "next" ? "md:items-end md:text-right" : ""}`}
      params={{ locale, slug: article.slug?.current ?? "" }}
      to="/$locale/playdata/news/$slug"
    >
      <span className="text-[1.3rem] text-play-pink uppercase tracking-[0.08em]">{label}</span>
      <span className="text-[1.5rem] leading-[1.5]">{article.title}</span>
    </Link>
  )
}

function RouteComponent() {
  const { locale: raw } = Route.useParams()
  const locale = raw as SupportedLocale
  const { post, siblings } = Route.useLoaderData() as NewsDetailPageData & {
    siblings: { next: NewsItem | null; previous: NewsItem | null }
  }
  const coverSrc = playdataNewsCoverSrc(post.slug?.current, post.newsCoverImage)

  return (
    <PlaydataSection>
      <article className="mx-auto flex max-w-[72ch] flex-col gap-[2.4rem]">
        <header className="flex flex-col items-center gap-[1.6rem] text-center">
          <h1 className="text-[2.8rem] leading-[1.4] md:text-[3.4rem]">{post.title}</h1>
          {post.newsCoverBlurb ? <p className="text-[1.4rem] leading-[1.7]">{post.newsCoverBlurb}</p> : null}
          {post.publishDate ? (
            <time
              className="text-[1.3rem]"
              dateTime={post.publishDate}
            >
              {playdataPublishDate(post.publishDate)}
            </time>
          ) : null}
          {/* Live carries a byline under the date. Absent until an editor
              sets one, which is every article ported off Squarespace. */}
          {post.author ? (
            <p className="text-[1.3rem]">
              {m.playdata_news_writtenBy({}, { locale })} {post.author}
            </p>
          ) : null}
        </header>
        {coverSrc ? (
          <PlaydataFigure
            alt=""
            height={450}
            src={coverSrc}
            width={800}
          />
        ) : null}
        {post.bio ? (
          <div
            className="playdata-prose flex flex-col gap-[1.4rem] text-[1.5rem] leading-[1.8]"
            // Markdown authored in the dashboard, rendered server-side and sanitised.
            dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(post.bio) }}
          />
        ) : null}
        {/*
         * Every article on the live site ends by offering the report, because
         * that is what the articles announce. Ours ended at the last
         * paragraph, so the one thing a reader might act on was missing.
         */}
        <aside className="mt-[1.6rem] flex flex-col items-center gap-[1.6rem] border-[#dce1e1] border-t pt-[2.4rem] text-center">
          <p className="max-w-[60ch] text-[1.5rem] leading-[1.8]">
            {m.playdata_news_reportCtaBody({}, { locale })}
          </p>
          <PlaydataButtonLink
            params={{ locale }}
            to="/$locale/contact"
          >
            {m.playdata_home_promo_cta({}, { locale })}
          </PlaydataButtonLink>
        </aside>

        {siblings.previous || siblings.next ? (
          <nav
            aria-label={m.playdata_news_meta_title({}, { locale })}
            className="grid gap-[1.6rem] border-[#dce1e1] border-t pt-[2.4rem] md:grid-cols-2"
          >
            {siblings.previous ? (
              <ArticleLink
                article={siblings.previous}
                direction="previous"
                label={m.playdata_news_previousArticle({}, { locale })}
                locale={locale}
              />
            ) : (
              <span />
            )}
            {siblings.next ? (
              <ArticleLink
                article={siblings.next}
                direction="next"
                label={m.playdata_news_nextArticle({}, { locale })}
                locale={locale}
              />
            ) : null}
          </nav>
        ) : null}

        <Link
          className="mt-[1.6rem] self-center text-[1.5rem] text-play-pink underline underline-offset-4"
          params={{ locale }}
          to="/$locale/playdata/news"
        >
          {m.playdata_common_backToNews({}, { locale })}
        </Link>
      </article>
    </PlaydataSection>
  )
}
